// src/hooks/useAdminDialogs.ts
import { useCallback } from 'react';

import type { AdminRow } from '@/types/administrators.types';
import type { UiConfirmState } from '@/types/ui.types';
import { useModal } from '@/hooks/useModal';
import { useConfirm } from '@/hooks/useConfirm';
import type { AdminUser } from '@/types/organization.types';

/**
 * Admin-specific convenience helpers for opening modals & confirms.
 */
export function useAdminDialogs() {
  const { open: openModal, close: closeModal } = useModal();
  const { open: openConfirm } = useConfirm();

  const openCreateAdmin = useCallback(() => {
    openModal({ type: 'createAdmin' });
  }, [openModal]);

  const openEditAdmin = useCallback((admin: AdminUser | AdminRow) => {
    openModal({ type: 'editAdmin', admin: admin as AdminUser });
  }, [openModal]);

  const confirmToggleActive = useCallback((row: AdminRow) => {
    const payload: UiConfirmState = { context: 'admins', kind: 'toggleActive', row };
    openConfirm(payload);
  }, [openConfirm]);

  const confirmForceLogout = useCallback((row: AdminRow) => {
    openConfirm({ context: 'admins', kind: 'forceLogout', row });
  }, [openConfirm]);

  const confirmForceLoginReset = useCallback((row: AdminRow) => {
    openConfirm({ context: 'admins', kind: 'forceLoginReset', row });
  }, [openConfirm]);

  const confirmDelete = useCallback((row: AdminRow) => {
    openConfirm({ context: 'admins', kind: 'delete', row });
  }, [openConfirm]);

  return {
    openCreateAdmin,
    openEditAdmin,
    confirmToggleActive,
    confirmForceLogout,
    confirmForceLoginReset,
    confirmDelete,
    closeModal,
  };
}
